import React, { useEffect, useRef, useState } from 'react';
import { getEventBadge, getEventDetail, isKeyEvent } from '@/components/wyscout/MatchEvents/helpers';
import type { MatchEvent } from '@/components/wyscout/MatchEvents/types';
import ConfirmModal from '@/components/ui/ConfirmModal';
import type { WyscoutEventVideo } from '@/services/api';
import { getV2Error } from '@/services/api/v2';
import { displayEventValue, eventActionLabel, eventChanceDetail, eventOutcome, isGoalEvent } from './helpers';
import type { EventTimelineProps } from './types';

const eventTeamName = (
  event: MatchEvent,
  homeTeamId: number,
  awayTeamId: number,
  homeTeamName: string,
  awayTeamName: string,
): string => {
  if (event.team?.id === homeTeamId) return homeTeamName;
  if (event.team?.id === awayTeamId) return awayTeamName;
  return event.team?.name || 'Unknown team';
};

const canRequestVideo = (event: MatchEvent, allowed: boolean): boolean => {
  if (!allowed || !isKeyEvent(event)) return false;
  const timestampValue = event.videoTimestamp.trim();
  const timestamp = Number(timestampValue);
  return timestampValue !== '' && Number.isFinite(timestamp) && timestamp >= 0;
};

const DetailPanel: React.FC<{
  event: MatchEvent;
  teamName: string;
  canWatchVideo: boolean;
  video: WyscoutEventVideo | null;
  videoError: string | null;
  watchButtonRef: React.RefObject<HTMLButtonElement | null>;
  closeButtonRef: React.RefObject<HTMLButtonElement | null>;
  onWatchVideo: (event: MatchEvent) => void;
  onCloseVideo: () => void;
  onViewGoalBuildUp?: (event: MatchEvent) => void;
}> = ({ event, teamName, canWatchVideo, video, videoError, watchButtonRef, closeButtonRef, onWatchVideo, onCloseVideo, onViewGoalBuildUp }) => {
  const action = eventActionLabel(event);
  const chanceDetail = eventChanceDetail(event);
  const outcome = eventOutcome(event);
  const metadata: Array<[string, string]> = [];

  if (chanceDetail) metadata.push(['Chance type', chanceDetail]);
  if (event.shot?.xg !== undefined) metadata.push(['xG', event.shot.xg.toFixed(2)]);
  if (event.shot?.postShotXg !== undefined) metadata.push(['Post-shot xG', event.shot.postShotXg.toFixed(2)]);
  if (event.shot?.bodyPart) metadata.push(['Body part', displayEventValue(event.shot.bodyPart)]);
  if (event.shot?.goalZone) metadata.push(['Goal zone', displayEventValue(event.shot.goalZone)]);
  if (event.pass?.recipient?.name) metadata.push(['Recipient', event.pass.recipient.name]);
  if (event.pass?.length !== undefined) metadata.push(['Pass length', `${Math.round(event.pass.length)}m`]);
  if (event.pass?.height) metadata.push(['Pass height', displayEventValue(event.pass.height)]);
  if (event.carry?.progression !== undefined) metadata.push(['Carry progression', `${event.carry.progression.toFixed(1)}m`]);
  if (event.possession?.attack?.flank) metadata.push(['Attack flank', displayEventValue(event.possession.attack.flank)]);

  return (
    <aside className="border-t border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50" aria-live="polite" aria-label="Selected event details">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Selected event</p>
          <h4 className="mt-1 font-semibold text-gray-900 dark:text-gray-100">{action}</h4>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWatchVideo && !video && (
            <button ref={watchButtonRef} type="button" onClick={() => onWatchVideo(event)} className="rounded-lg bg-amber-600 px-3 py-2 text-sm font-semibold text-white hover:bg-amber-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900">
              Watch 20s clip
            </button>
          )}
          {isGoalEvent(event) && onViewGoalBuildUp && (
            <button
              type="button"
              onClick={() => onViewGoalBuildUp(event)}
              className="rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white hover:bg-green-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
            >
              View goal build-up
            </button>
          )}
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
        <div><dt className="text-xs text-gray-500 dark:text-gray-400">Minute</dt><dd className="font-medium text-gray-900 dark:text-gray-100">{event.minute}'</dd></div>
        <div><dt className="text-xs text-gray-500 dark:text-gray-400">Action</dt><dd className="font-medium text-gray-900 dark:text-gray-100">{action}</dd></div>
        <div><dt className="text-xs text-gray-500 dark:text-gray-400">Player</dt><dd className="font-medium text-gray-900 dark:text-gray-100">{event.player?.name || 'Unknown player'}</dd></div>
        <div><dt className="text-xs text-gray-500 dark:text-gray-400">Team</dt><dd className="font-medium text-gray-900 dark:text-gray-100">{teamName}</dd></div>
        <div><dt className="text-xs text-gray-500 dark:text-gray-400">Outcome</dt><dd className="font-medium capitalize text-gray-900 dark:text-gray-100">{outcome}</dd></div>
        {metadata.map(([label, value]) => <div key={label}><dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt><dd className="font-medium text-gray-900 dark:text-gray-100">{value}</dd></div>)}
      </dl>
      {videoError && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200">{videoError}</p>}
      {video && (
        <div className="mt-4 space-y-2">
          <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
            <video
              aria-label="20 second event clip"
              src={video.url}
              controls
              autoPlay
              preload="metadata"
              className="h-full w-full"
            />
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{video.quality.toUpperCase()} quality</p>
            <button ref={closeButtonRef} type="button" onClick={onCloseVideo} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800">
              Close
            </button>
          </div>
        </div>
      )}
    </aside>
  );
};

const EventTimeline: React.FC<EventTimelineProps> = ({
  events,
  canWatchVideo,
  requestEventVideo,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  selectedEventId,
  onSelectEvent,
  onViewGoalBuildUp,
  emphasizedEventIds,
  eventRelationshipLabels,
  interleavedContent,
}) => {
  const selectedEvent = events.find(event => event.id === selectedEventId);
  const [confirmEvent, setConfirmEvent] = useState<MatchEvent | null>(null);
  const [video, setVideo] = useState<WyscoutEventVideo | null>(null);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [loadingVideo, setLoadingVideo] = useState(false);
  const requestVersion = useRef(0);
  const watchButtonRef = useRef<HTMLButtonElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const restoreWatchFocus = useRef(false);

  useEffect(() => {
    requestVersion.current += 1;
    setConfirmEvent(null);
    setVideo(null);
    setVideoError(null);
    setLoadingVideo(false);
    restoreWatchFocus.current = false;
  }, [selectedEventId]);

  useEffect(() => {
    if (!canWatchVideo) setConfirmEvent(null);
  }, [canWatchVideo]);

  useEffect(() => {
    if (video) {
      closeButtonRef.current?.focus();
    } else if (restoreWatchFocus.current) {
      restoreWatchFocus.current = false;
      watchButtonRef.current?.focus();
    }
  }, [video]);

  const confirmVideoRequest = async (): Promise<void> => {
    const currentEvent = confirmEvent && events.find(event => event.id === confirmEvent.id);
    if (!currentEvent || loadingVideo || currentEvent.id !== selectedEventId || !canRequestVideo(currentEvent, canWatchVideo)) {
      setConfirmEvent(null);
      return;
    }
    const eventId = currentEvent.id;
    const version = ++requestVersion.current;
    setLoadingVideo(true);
    setVideoError(null);
    try {
      const nextVideo = await requestEventVideo(eventId);
      if (requestVersion.current === version) setVideo(nextVideo);
    } catch (error) {
      if (requestVersion.current === version) setVideoError(getV2Error(error).message);
    } finally {
      if (requestVersion.current === version) {
        setLoadingVideo(false);
        setConfirmEvent(null);
      }
    }
  };

  const closeVideo = (): void => {
    requestVersion.current += 1;
    restoreWatchFocus.current = true;
    setVideo(null);
  };

  return (
    <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900" aria-labelledby="event-timeline-title">
      <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-700">
        <h3 id="event-timeline-title" className="font-semibold text-gray-900 dark:text-gray-100">Timeline</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">{events.length} matching {events.length === 1 ? 'event' : 'events'}</p>
      </div>

      <div className="max-h-[34rem] flex-1 overflow-y-auto p-2">
        {events.length === 0 ? (
          interleavedContent?.get(0) ?? <p className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">No events match these filters.</p>
        ) : <>{events.map((event, index) => {
          const badge = getEventBadge(event);
          const action = eventActionLabel(event);
          const chanceDetail = eventChanceDetail(event);
          const detail = getEventDetail(event);
          const visibleDetail = chanceDetail && detail.toLowerCase() === chanceDetail.toLowerCase() ? '' : detail;
          const selected = event.id === selectedEventId;
          const emphasized = emphasizedEventIds?.has(event.id) ?? false;
          const relationshipLabel = eventRelationshipLabels?.get(event.id);
          const teamName = eventTeamName(event, homeTeamId, awayTeamId, homeTeamName, awayTeamName);
          const isHome = event.team?.id === homeTeamId;

          return (
            <React.Fragment key={event.id}>
              {interleavedContent?.get(index)}
              <div
              data-player-moment={emphasized ? '' : undefined}
              aria-label={relationshipLabel ? `${relationshipLabel} event` : undefined}
              className={`mb-1 rounded-lg border ${selected ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-950/30' : emphasized ? 'border-club-gold-400 bg-club-gold-50/70 dark:border-club-gold-500 dark:bg-club-gold-950/20' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-800/60'} ${emphasized ? 'ring-2 ring-club-gold-400 dark:ring-club-gold-500' : ''}`}
            >
              <button
                type="button"
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelectEvent(event.id)}
                className="flex w-full items-start gap-3 rounded-lg px-3 py-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
              >
                <span className="w-10 shrink-0 text-right font-mono text-sm font-semibold text-gray-700 dark:text-gray-300">{event.minute}'</span>
                <span className={`mt-1 h-5 w-1.5 shrink-0 rounded-full ${isHome ? 'bg-blue-500' : 'bg-rose-500'}`} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${badge.color}`}>{action}</span>
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{event.player?.name || 'Unknown player'}</span>
                    {relationshipLabel && <span className="rounded-full bg-club-gold-100 px-2 py-0.5 text-xs font-semibold text-club-navy-900 dark:bg-club-gold-800 dark:text-white">{relationshipLabel}</span>}
                  </span>
                  <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{teamName}{chanceDetail ? ` | ${chanceDetail}` : ''}{visibleDetail ? ` | ${visibleDetail}` : ''}</span>
                </span>
              </button>
              {isGoalEvent(event) && onViewGoalBuildUp && (
                <div className="px-3 pb-2 pl-16">
                  <button type="button" onClick={() => onViewGoalBuildUp(event)} className="text-xs font-semibold text-green-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 dark:text-green-400">
                    View goal build-up
                  </button>
                </div>
              )}
              </div>
            </React.Fragment>
          );
        })}{interleavedContent?.get(events.length)}</>}
      </div>

      {selectedEvent && (
        <DetailPanel
          event={selectedEvent}
          teamName={eventTeamName(selectedEvent, homeTeamId, awayTeamId, homeTeamName, awayTeamName)}
          canWatchVideo={canRequestVideo(selectedEvent, canWatchVideo)}
          video={video}
          videoError={videoError}
          watchButtonRef={watchButtonRef}
          closeButtonRef={closeButtonRef}
          onWatchVideo={event => {
            setVideoError(null);
            setConfirmEvent(event);
          }}
          onCloseVideo={closeVideo}
          onViewGoalBuildUp={onViewGoalBuildUp}
        />
      )}
      <ConfirmModal
        isOpen={Boolean(confirmEvent)}
        title="Generate Wyscout clip?"
        message="Generating this clip uses 20 seconds of the shared Wyscout video allowance. Continue?"
        confirmLabel="Generate clip"
        tone="amber"
        loading={loadingVideo}
        onClose={() => setConfirmEvent(null)}
        onConfirm={() => { void confirmVideoRequest(); }}
      />
    </section>
  );
};

export default EventTimeline;
